import { type FormEvent, type ReactNode, useContext } from 'react'
import type { FieldValues } from 'react-hook-form'
import { FormLocalizationContext } from '../FormLocalizationProvider'
import type { UseFormReturn } from '../wrappers'
import { useFormSubmit } from './useFormSubmit'

export const submitButtonTextDefinition = ['save', 'send', 'add'] as const
export type SubmitButtonText = (typeof submitButtonTextDefinition)[number]

export type ReactHookFormProps<FV extends FieldValues = FieldValues> = {
  onSubmit: (values: FV) => Promise<boolean> | boolean
  form: UseFormReturn<FV>
  additionalButtons?: ReactNode
  isSubmitButtonDisabled?: boolean
  isSuccessMessageDisplayed?: boolean
  customSubmitSuccessText?: ReactNode
} & (
  | {
      submitButtonText: SubmitButtonText
      customSubmitButtonText?: never
    }
  | {
      submitButtonText?: never
      customSubmitButtonText: ReactNode
    }
)

export const useFormLogic = <FV extends FieldValues = FieldValues>(
  props: ReactHookFormProps<FV>
): {
  isLoading: boolean
  onSubmit: (e: FormEvent<HTMLElement>) => void
  rawOnSubmit: () => void
  additionalButtons?: ReactNode
  hasUnsavedChanges: boolean
  isSubmitButtonDisabled: boolean
  submitButtonText: ReactNode
  dirtyFieldsCount: number
  reset: () => void
} => {
  const {
    onSubmit: originalOnSubmit,
    isSubmitButtonDisabled,
    form,
    submitButtonText = 'send',
    customSubmitButtonText,
    customSubmitSuccessText,
    additionalButtons,
    isSuccessMessageDisplayed = true,
  } = props
  const { submitButton } = useContext(FormLocalizationContext)

  const { submit, isLoading } = useFormSubmit({
    form,
    onSubmit: originalOnSubmit,
    isSuccessMessageDisplayed,
    customSubmitSuccessText: customSubmitSuccessText as string,
    submitButtonText: submitButtonText as SubmitButtonText,
  })

  const onSubmit = (e: FormEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    void form.handleSubmit(async vals => {
      await submit(vals)
    })(e)
  }

  const rawOnSubmit = () => {
    submit()
  }

  const submitButtonFormattedText =
    customSubmitButtonText ?? submitButton[submitButtonText as SubmitButtonText]

  return {
    isLoading,
    onSubmit,
    rawOnSubmit,
    additionalButtons,
    hasUnsavedChanges: form.formState.isDirty,
    isSubmitButtonDisabled:
      isSubmitButtonDisabled !== undefined ? isSubmitButtonDisabled : !form.formState.isDirty,
    submitButtonText: submitButtonFormattedText,
    dirtyFieldsCount: Object.keys(form.formState.dirtyFields).length,
    reset: form.reset,
  }
}
